/**
 * Every write to a page (`111-frontend-architecture.md` §4, `112-offline-drafts.md` §5).
 *
 * **Not one of these calls `fetch`.** Every `mutationFn` hands an entry to the outbox and
 * resolves as soon as it is in IndexedDB — before the network is touched. That is what
 * makes the optimistic tree HONEST when offline: the page really does exist, durably, on
 * this device.
 *
 * Consequently `onError` does NOT fire when the network is down — the write is queued and
 * the optimistic state stands. It fires only for a permanent failure the outbox will not
 * retry. Rolling back on a mere network blip would be the bug this whole layer exists to
 * prevent.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import {
  emptyDoc,
  getArchivedTree,
  getBacklinks,
  getPage,
  getTree,
  type BacklinksResponse,
  type PageDto,
  type TreePage,
  type TreeResponse,
  UNCONFIRMED,
} from '@/api/endpoints/pages';
import { toast } from '@/components/ui/sonner';
import { outbox } from '@/offline/outbox';
import { setExpanded } from '@/stores/treeExpanded';
import { translate } from '@/lib/i18n';

/* ── Reads ──────────────────────────────────────────────────────────────────── */

export function useTree(): UseQueryResult<TreeResponse> {
  return useQuery({
    queryKey: keys.pages.tree(),
    queryFn: getTree,
    // We are the only writer, and every mutation invalidates this explicitly (`111` §3.2).
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function useTrash(enabled: boolean): UseQueryResult<TreeResponse> {
  return useQuery({
    queryKey: keys.pages.trash(),
    queryFn: getArchivedTree,
    enabled, // fetched lazily, on first expand of the Trash section (`060` §5)
    staleTime: 60_000,
  });
}

export function usePage(id: string | undefined): UseQueryResult<PageDto> {
  return useQuery({
    queryKey: keys.pages.detail(id ?? ''),
    queryFn: () => getPage(id as string),
    enabled: id !== undefined,
    // Shorter than the tree's, because the outbox may land a queued write from ANOTHER
    // tab of the same app.
    staleTime: 30_000,
    gcTime: 15 * 60_000,
  });
}

/**
 * The backlinks panel (`070` §7.6). Derived server-side from `page_link` — the client never
 * sends the link list, and never computes this from the document (§7.3).
 *
 * Invalidated on EVERY content save, for every page, because `PageLinkSynchronizer` may
 * have changed some other page's backlink list and we cannot know which (`111` §3.3).
 */
export function useBacklinks(id: string | undefined): UseQueryResult<BacklinksResponse> {
  return useQuery({
    queryKey: keys.pages.backlinks(id ?? ''),
    queryFn: () => getBacklinks(id as string),
    enabled: id !== undefined,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* ── Create (§4.1) ──────────────────────────────────────────────────────────── */

export interface CreatePageInput {
  /** The client-generated ULID. The server never invents one for a client create. */
  id: string;
  parentId: string | null;
  position: string;
  title?: string;
}

export function useCreatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePageInput) =>
      outbox.enqueue({
        kind: 'page.create',
        entityId: input.id,
        label: input.title ?? 'Untitled',
        request: {
          method: 'POST',
          path: '/api/pages',
          body: {
            id: input.id,
            parentId: input.parentId,
            position: input.position,
            title: input.title ?? 'Untitled',
          },
        },
      }),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: keys.pages.tree() });
      const previous = queryClient.getQueryData<TreeResponse>(keys.pages.tree());

      const row: TreePage = {
        id: input.id, // ← the real, FINAL id. There is no temp-id reconciliation anywhere.
        parentId: input.parentId,
        position: input.position,
        title: input.title ?? 'Untitled',
        icon: null,
        isFavorite: false,
        hasChildren: false,
        dueAt: null,
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueryData<TreeResponse>(
        keys.pages.tree(),
        (old) => old && { ...old, pages: [...old.pages, row] },
      );

      // Seed the detail cache, so navigating to the page we just made renders instantly
      // rather than showing a skeleton for a page we already know everything about.
      const detail: PageDto = {
        id: input.id,
        parentId: input.parentId,
        position: input.position,
        title: input.title ?? 'Untitled',
        icon: null,
        content: emptyDoc(),
        contentVersion: 1,
        dueAt: null,
        dueAllDay: true,
        isFavorite: false,
        isArchived: false,
        tags: [],
        breadcrumb: [],
        createdAt: row.updatedAt,
        // NOT a client-invented timestamp — see UNCONFIRMED. Sending a fabricated
        // `If-Unmodified-Since` would 412 the first save of every new page.
        updatedAt: UNCONFIRMED,
      };
      queryClient.setQueryData(keys.pages.detail(input.id), detail);

      // You always see the page you just made.
      if (input.parentId !== null) setExpanded(input.parentId, true);

      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(keys.pages.tree(), context.previous);
      toast.error(translate('notebook.toast.createError'));
    },
  });
}

/* ── Rename / icon / favorite ───────────────────────────────────────────────── */

export interface UpdatePageInput {
  id: string;
  title?: string;
  icon?: string | null;
  isFavorite?: boolean;
}

/**
 * `page.update` is coalescable, and it is the SAME kind the editor's autosave uses. That
 * is deliberate: a title save and a content save for the same page merge into one request
 * carrying both, and the merge keeps the FIRST entry's `ifUnmodifiedSince`
 * (`112-offline-drafts.md` §5.2). A title-only save must not drop a queued content save.
 */
export function useUpdatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...patch }: UpdatePageInput) =>
      outbox.enqueue({
        kind: 'page.update',
        entityId: id,
        label: patch.title ?? 'page',
        request: { method: 'PATCH', path: `/api/pages/${id}`, body: patch },
      }),

    onMutate: async ({ id, ...patch }) => {
      await queryClient.cancelQueries({ queryKey: keys.pages.tree() });
      const previous = queryClient.getQueryData<TreeResponse>(keys.pages.tree());

      queryClient.setQueryData<TreeResponse>(
        keys.pages.tree(),
        (old) =>
          old && {
            ...old,
            pages: old.pages.map((page) => (page.id === id ? { ...page, ...patch } : page)),
          },
      );

      // The header and the tree edit the same field and must stay in sync through the
      // cache (`060` §6, "Renaming").
      queryClient.setQueryData<PageDto>(
        keys.pages.detail(id),
        (old) => old && { ...old, ...patch },
      );

      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(keys.pages.tree(), context.previous);
    },
  });
}

/* ── Move (§4.2) ────────────────────────────────────────────────────────────── */

export interface MovePageInput {
  id: string;
  parentId: string | null;
  position: string;
}

export function useMovePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, parentId, position }: MovePageInput) =>
      outbox.enqueue({
        kind: 'page.move',
        entityId: id,
        request: {
          method: 'POST',
          path: `/api/pages/${id}/move`,
          body: { parentId, position },
        },
      }),

    // The tree must reorder UNDER THE CURSOR. A drag that waits 80ms for the network
    // before the row lands feels broken.
    onMutate: async ({ id, parentId, position }) => {
      await queryClient.cancelQueries({ queryKey: keys.pages.tree() });
      const previous = queryClient.getQueryData<TreeResponse>(keys.pages.tree());

      queryClient.setQueryData<TreeResponse>(
        keys.pages.tree(),
        (old) =>
          old && {
            ...old,
            // A one-field patch to one item. This is the payoff for the flat array.
            pages: old.pages.map((page) =>
              page.id === id ? { ...page, parentId, position } : page,
            ),
          },
      );

      if (parentId !== null) setExpanded(parentId, true);

      return { previous };
    },

    onError: (_error, _input, context) => {
      // A 409 (the move would have created a cycle) is reported by the outbox bridge,
      // which also re-invalidates the tree. This is the rollback for everything else.
      if (context?.previous) queryClient.setQueryData(keys.pages.tree(), context.previous);
    },
  });
}

/* ── Archive / restore / delete (`060` §4) ──────────────────────────────────── */

/**
 * Archive cascades to the whole subtree — SERVER-SIDE. Do not try to compute the cascade
 * optimistically: it is a subtree walk the server has already done, and getting it wrong
 * means showing a page that no longer exists (`060` §4).
 *
 * We do remove the page itself from the tree immediately, because the row the user just
 * archived staying put until a refetch lands reads as a broken button.
 */
export function useArchivePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      outbox.enqueue({
        kind: 'page.archive',
        entityId: id,
        request: { method: 'POST', path: `/api/pages/${id}/archive` },
      }),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: keys.pages.tree() });
      const previous = queryClient.getQueryData<TreeResponse>(keys.pages.tree());

      queryClient.setQueryData<TreeResponse>(
        keys.pages.tree(),
        (old) => old && { ...old, pages: old.pages.filter((page) => page.id !== id) },
      );

      return { previous };
    },

    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(keys.pages.tree(), context.previous);
    },
  });
}

export function useRestorePage() {
  return useMutation({
    mutationFn: (id: string) =>
      outbox.enqueue({
        kind: 'page.restore',
        entityId: id,
        request: { method: 'POST', path: `/api/pages/${id}/restore` },
      }),
  });
}

/** Only permitted on an already-archived page. Deletion is always two steps. */
export function useDeletePage() {
  return useMutation({
    mutationFn: (id: string) =>
      outbox.enqueue({
        kind: 'page.delete',
        entityId: id,
        request: { method: 'DELETE', path: `/api/pages/${id}` },
      }),
  });
}
